import React, { useState, useEffect } from 'react';
import { Navigate, useParams } from "react-router-dom";
import { prettyPrintJson, FormatOptions } from 'pretty-print-json';
import "@dndViews/floating_feature/pretty-print-json.css";
import {
  ArrowBack
} from "@mui/icons-material";
import { Tooltip, Fab } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  FloatingFeature, 
} from "@models";

import API from "@dndUtilities/system_api";

import ButtonBox from "@components/input/buttons/ButtonBox";
import FloatingFeatureInput from "@dndComponents/feature/FloatingFeatureInput";
import FloatingFeatureBox from "@dndComponents/FloatingFeatureBox";

import LoadingBox from "@components/input/LoadingBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";

const prettyPrintJsonOptions: FormatOptions = { 
  indent: 4, 
};

const api = API.getInstance();

export default function FloatingFeatureEdit() {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [floating_features, setFloatingFeatures] = useState<FloatingFeature[]>([]);
  const [processing, setProcessing] = useState(false);
  const [view, setView] = useState("Edit");
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [obj, setObj] = useState<FloatingFeature>(new FloatingFeature());
  const [merge_floating_feature, setMergeFloatingFeature] = useState<FloatingFeature | null>(null);

  useEffect(() => {
    if(!fetched) {
      load();
    }
  });

  useEffect(() => {
    if (loading) {
      if (id && id !== "") {
        load_object(id);
      } else {
        // It's here from clicking Create on index.  
        // If the user is not admin, then we need to give the floating feature their id as owner or it will be disabled.
        const new_obj = obj;
        if (api.user && !api.user.admin && new_obj.name === "" && new_obj.owner_id === "") {
          new_obj.owner_id = api.user._id;
        }
        setObj(new_obj);
        setLoading(false);
      }
    }
    // eslint-disable-next-line
  }, [floating_features]);

  const load = () => {
    setFetched(true);
    setLoading(true);
    api.getObjectsOld("floating_feature").then(res => {
      setFloatingFeatures(res as FloatingFeature[]);
    });
  }

  // Loads the editing obj into state
  const load_object = (id: string) => {
    const objFinder = floating_features.find(a => a._id === id);
    if (objFinder) {
      setObj(objFinder.clone());
      setLoading(false);
    }
  }
  
  const merge = () => {
    if (merge_floating_feature) {
      const new_obj = obj;
      new_obj.merged_ids.push();
      new_obj.merged_ids = [
        ...new_obj.merged_ids,
        merge_floating_feature._id,
        ...merge_floating_feature.merged_ids
      ];
      setObj(new_obj);
      setProcessing(true);
      api.upsertObject("floating_feature", new_obj).then(res => {
        api.deleteObject("floating_feature", merge_floating_feature).then(res2 => {
          setMergeFloatingFeature(null);
          setProcessing(false);
        });
      });
    }
  }

  if (!fetched || loading) {
    const height = window.innerHeight - 64;
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth} />);
  } else if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else { 
    console.log(obj);
    const formHeight = window.innerHeight - 64;
    return (
      <div
        style={{ 
          height: `${formHeight}px`,
        }}>
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 4
            }}>
            <Tooltip title={`Back to Features`}>
              <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                onClick={ () => {
                  setNavigateTo(`/dandd/floating_feature`);
                }}>
                <ArrowBack/>
              </Fab>
            </Tooltip> 
            <span className="header">
              { obj._id === "" ? "Create Feature" : `Edit ${obj.name}` }
            </span>
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            { api.user && api.user.admin && 
              <span><CenteredMenu options={["Edit","Merge"]} selected={view} onSelect={setView} /></span>
            }
          </Grid>
          <Grid
            size={{
              xs: 4
            }}></Grid>
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            { view === "Merge" && obj._id !== "" && !processing ?
              <>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <FloatingFeatureBox
                    name="Merge Feature"
                    default_search={ obj.name }
                    feature_type={ obj.feature_type }
                    limit={100}
                    floating_feature_id={ merge_floating_feature ? merge_floating_feature._id : "" }
                    onFloatingFeatureSelected={setMergeFloatingFeature}
                  />
                </Grid>
                { merge_floating_feature &&
                  <Grid
                    container
                    spacing={0}
                    direction="row"
                    size={{
                      xs: 12
                    }}>
                    <Grid
                      size={{
                        xs: 6
                      }}>This one</Grid>
                    <Grid
                      size={{
                        xs: 6
                      }}>Merging</Grid>
                    <Grid
                      size={{
                        xs: 6
                      }}>
                      <section className="pretty-print">
                        <output className="json-container" 
                          dangerouslySetInnerHTML={{ 
                            __html: prettyPrintJson.toHtml(
                              obj.toDBObj(), 
                              prettyPrintJsonOptions) 
                          }}>
                        </output>
                      </section>
                    </Grid>
                    <Grid
                      size={{
                        xs: 6
                      }}>
                      <section className="pretty-print">
                        <output className="json-container" 
                          dangerouslySetInnerHTML={{ 
                            __html: prettyPrintJson.toHtml(
                              merge_floating_feature.toDBObj(), 
                              prettyPrintJsonOptions) 
                          }}>
                        </output>
                      </section>
                    </Grid>
                  </Grid>
                }
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <ButtonBox 
                    name="Merge"
                    disabled={ !merge_floating_feature || merge_floating_feature._id === obj._id || processing }
                    onClick={merge}
                  />
                </Grid>
              </>
            :
              <Grid
                size={{
                  xs: 12
                }}>
                <FloatingFeatureInput 
                  floating_feature={obj}
                  height={formHeight - 40}
                  // onChange={(feature: FloatingFeature) => {
                  //   // Shouldn't be necessary
                  // }}
                  // disabled={this.props.disabled}
                  onDelete={() => {
                    setNavigateTo(`/dandd/floating_feature`);
                  }}
                  onAdd2={setObj}
                />
              </Grid>
            }
          </Grid>
        </Grid>
      </div>
    ); 
  }
}